import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";
import { hostname } from "../../utils";
import { SKILL_SOURCE_CLAWHUB, SKILL_SOURCE_CUSTOM } from "../../constants";

interface Skill {
  id: string;
  slug: string;
  name: string;
  description: string;
  source: string;
  sourceUrl?: string;
  version?: string;
  author?: string;
  content: string;
  status: string;
  priority: number;
  timesUsed: number;
  installedAt: string;
  skillMetadata?: Record<string, unknown>;
}

interface RegistrySkill {
  slug: string;
  name: string;
  description: string;
  author?: string;
  downloads: number;
  installs: number;
  stars: number;
  category?: string;
  tags?: string[];
  sourceUrl?: string;
  installed?: boolean;
}

interface SkillsTabProps {
  getToken: () => Promise<string>;
}

// Format large numbers (e.g., 270540 -> "270.5K")
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toString();
};

const SkillsTab: React.FC<SkillsTabProps> = ({ getToken }) => {
  const [installedSkills, setInstalledSkills] = useState<Skill[]>([]);
  const [registrySkills, setRegistrySkills] = useState<RegistrySkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegistry, setLoadingRegistry] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBrowser, setShowBrowser] = useState(false);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [installingSlug, setInstallingSlug] = useState<string | null>(null);
  const [deleteConfirmSkillId, setDeleteConfirmSkillId] = useState<string | null>(null);

  // Custom skill form state
  const [customName, setCustomName] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customContent, setCustomContent] = useState("");
  const [savingCustom, setSavingCustom] = useState(false);

  // Install from URL state
  const [installUrl, setInstallUrl] = useState("");
  const [installingFromUrl, setInstallingFromUrl] = useState(false);

  // Debounce timer ref
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const loadInstalledSkills = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/personal-assistant/skills`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInstalledSkills(response.data.skills || []);
    } catch (err) {
      console.error("Error loading installed skills:", err);
      setError("Failed to load installed skills");
    }
  }, [getToken]);

  const loadRegistrySkills = useCallback(async (search?: string) => {
    setLoadingRegistry(true);
    try {
      const token = await getToken();
      const params: Record<string, string> = {};
      if (search) params.search = search;

      const response = await axios.get(`${hostname}/personal-assistant/skills/registry`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRegistrySkills(response.data.skills || []);
    } catch (err) {
      console.error("Error loading registry skills:", err);
    } finally {
      setLoadingRegistry(false);
    }
  }, [getToken]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadInstalledSkills();
      setLoading(false);
    };
    init();
  }, [loadInstalledSkills]);

  useEffect(() => {
    if (showBrowser) {
      // Debounce search - wait 500ms after typing stops
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        loadRegistrySkills(searchQuery);
      }, searchQuery ? 500 : 0); // No delay on initial load

      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    }
  }, [showBrowser, searchQuery, loadRegistrySkills]);

  const handleToggleSkill = async (skillId: string) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/personal-assistant/skills/${skillId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadInstalledSkills();
    } catch (err) {
      console.error("Error toggling skill:", err);
      setError("Failed to toggle skill");
    }
  };

  const handleDeleteSkill = async (skillId: string) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/personal-assistant/skills/${skillId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadInstalledSkills();
    } catch (err) {
      console.error("Error deleting skill:", err);
      setError("Failed to delete skill");
    } finally {
      setDeleteConfirmSkillId(null);
    }
  };

  const handleInstallFromRegistry = async (slug: string) => {
    setInstallingSlug(slug);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/personal-assistant/skills`,
        { slug, source: "clawhub" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadInstalledSkills();
      await loadRegistrySkills(searchQuery);
    } catch (err) {
      console.error("Error installing skill:", err);
      setError("Failed to install skill");
    } finally {
      setInstallingSlug(null);
    }
  };

  const handleInstallFromUrl = async () => {
    if (!installUrl.trim()) return;

    // Extract slug from ClawHub URL (e.g., https://clawhub.ai/skills/my-skill)
    const urlMatch = installUrl.match(/clawhub\.ai\/skills\/([a-zA-Z0-9-_]+)/);
    const slug = urlMatch ? urlMatch[1] : installUrl.trim();

    setInstallingFromUrl(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/personal-assistant/skills`,
        { slug, source: "clawhub" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadInstalledSkills();
      if (showBrowser) {
        await loadRegistrySkills(searchQuery);
      }
      setInstallUrl("");
    } catch (err) {
      console.error("Error installing skill from URL:", err);
      setError("Failed to install skill. Check the URL and try again.");
    } finally {
      setInstallingFromUrl(false);
    }
  };

  const handleSaveCustomSkill = async () => {
    if (!customName.trim() || !customContent.trim()) {
      setError("Name and content are required");
      return;
    }

    setSavingCustom(true);
    try {
      const token = await getToken();

      if (editingSkill) {
        // Update existing skill
        await axios.put(
          `${hostname}/personal-assistant/skills/${editingSkill.id}`,
          {
            name: customName,
            description: customDescription,
            content: customContent,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new skill
        await axios.post(
          `${hostname}/personal-assistant/skills`,
          {
            name: customName,
            description: customDescription,
            content: customContent,
            source: "custom",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      await loadInstalledSkills();
      handleCloseCustomDialog();
    } catch (err) {
      console.error("Error saving custom skill:", err);
      setError("Failed to save skill");
    } finally {
      setSavingCustom(false);
    }
  };

  const handleOpenEditDialog = (skill: Skill) => {
    setEditingSkill(skill);
    setCustomName(skill.name);
    setCustomDescription(skill.description || "");
    setCustomContent(skill.content);
    setShowCustomDialog(true);
  };

  const handleOpenCreateDialog = () => {
    setEditingSkill(null);
    setCustomName("");
    setCustomDescription("");
    setCustomContent(`# Skill Instructions

## When to Use
Describe when this skill should be activated.

## Instructions
Step-by-step instructions for the assistant to follow.

## Examples
Provide examples of inputs and expected outputs.
`);
    setShowCustomDialog(true);
  };

  const handleCloseCustomDialog = () => {
    setShowCustomDialog(false);
    setEditingSkill(null);
    setCustomName("");
    setCustomDescription("");
    setCustomContent("");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Skills teach your assistant new capabilities. Install from ClawHub or create your own.
      </Typography>

      <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ py: 0.5 }}>
        <Typography variant="caption">
          Active skills are listed in <strong>SKILLS.md</strong> with their name and description.
          Your assistant uses <code>activate_skill</code> to sync full instructions to your workstation.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setShowBrowser(true)}
        >
          Browse Skills
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create Custom
        </Button>
      </Box>

      {/* Installed Skills */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Installed Skills ({installedSkills.length})
        </Typography>

        {installedSkills.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: "action.hover",
              borderRadius: 1,
            }}
          >
            <Typography color="text.secondary">
              No skills installed yet. Browse the registry or create a custom skill.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {installedSkills.map((skill) => (
              <Card key={skill.id} variant="outlined">
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title={skill.status === "active" ? "Disable" : "Enable"}>
                      <Switch
                        size="small"
                        checked={skill.status === "active"}
                        onChange={() => handleToggleSkill(skill.id)}
                      />
                    </Tooltip>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {skill.name}
                        </Typography>
                        <Chip
                          label={skill.source === SKILL_SOURCE_CLAWHUB ? "ClawHub" : "Custom"}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem", flexShrink: 0 }}
                        />
                      </Box>
                      {skill.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {skill.description}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      {skill.source === SKILL_SOURCE_CUSTOM && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(skill)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Uninstall">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmSkillId(skill.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Skill Browser Dialog */}
      <Dialog
        open={showBrowser}
        onClose={() => setShowBrowser(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Browse Skills
            <Button
              size="small"
              variant="text"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => window.open("https://clawhub.ai", "_blank")}
              sx={{ ml: 0.5, textTransform: "none", fontSize: "0.75rem" }}
            >
              ClawHub
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => loadRegistrySkills(searchQuery)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setShowBrowser(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, overflow: "hidden", px: 2 }}>
          {/* Search and Install from URL */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadRegistrySkills(searchQuery)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              placeholder="Paste ClawHub URL or slug..."
              value={installUrl}
              onChange={(e) => setInstallUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInstallFromUrl()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: installUrl && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleInstallFromUrl}
                      disabled={installingFromUrl}
                      sx={{ minWidth: 0, px: 1 }}
                    >
                      {installingFromUrl ? <CircularProgress size={16} /> : "Install"}
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Skills list */}
          {loadingRegistry ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : registrySkills.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No skills found. Try a different search.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflow: "auto", flex: 1 }}>
              {registrySkills.map((skill) => (
                <Box
                  key={skill.slug}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {skill.name}
                        </Typography>
                        <Tooltip title="View on ClawHub">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://clawhub.ai/skills/${skill.slug}`, "_blank")}
                            sx={{ p: 0.25 }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        {skill.category && (
                          <Chip
                            label={skill.category}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </Box>
                      {skill.description && (
                        <Tooltip title={skill.description} enterDelay={500}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.8rem",
                              lineHeight: 1.4,
                              mb: 0.75,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {skill.description}
                          </Typography>
                        </Tooltip>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        {skill.author && (
                          <Typography variant="caption" color="text.secondary">
                            {skill.author}
                          </Typography>
                        )}
                        {skill.downloads > 0 && (
                          <Tooltip title={`${skill.downloads.toLocaleString()} downloads`}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <DownloadIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatNumber(skill.downloads)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                        {skill.stars > 0 && (
                          <Tooltip title={`${skill.stars.toLocaleString()} stars`}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <StarIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatNumber(skill.stars)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    <Button
                      variant={skill.installed ? "outlined" : "contained"}
                      size="small"
                      onClick={() => handleInstallFromRegistry(skill.slug)}
                      disabled={installingSlug === skill.slug || skill.installed}
                      sx={{ minWidth: 70, flexShrink: 0 }}
                    >
                      {installingSlug === skill.slug ? (
                        <CircularProgress size={16} />
                      ) : skill.installed ? (
                        "Installed"
                      ) : (
                        "Install"
                      )}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Skill Dialog */}
      <Dialog
        open={showCustomDialog}
        onClose={handleCloseCustomDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {editingSkill ? "Edit Skill" : "Create Custom Skill"}
          <IconButton size="small" onClick={handleCloseCustomDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Skill Name"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            fullWidth
            required
            placeholder="e.g., Email Summarizer"
          />
          <TextField
            label="Description"
            value={customDescription}
            onChange={(e) => setCustomDescription(e.target.value)}
            fullWidth
            placeholder="Brief description of what this skill does"
          />
          <TextField
            label="Instructions (Markdown)"
            value={customContent}
            onChange={(e) => setCustomContent(e.target.value)}
            fullWidth
            multiline
            required
            minRows={15}
            maxRows={25}
            InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem" } }}
            helperText="Write instructions for your assistant to follow when this skill is activated"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseCustomDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCustomSkill}
            disabled={savingCustom || !customName.trim() || !customContent.trim()}
          >
            {savingCustom ? <CircularProgress size={20} /> : editingSkill ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmSkillId !== null}
        onClose={() => setDeleteConfirmSkillId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Uninstall Skill</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to uninstall this skill?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmSkillId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmSkillId && handleDeleteSkill(deleteConfirmSkillId)}
            color="error"
            variant="contained"
          >
            Uninstall
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillsTab;
